import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeProvider';
import axios from 'axios';
import { 
  Clock, 
  Plus, 
  Trash2, 
  Settings, 
  Timer,
  Flag,
  Car,
  Maximize,
  Minimize,
  Cloud,
  Wind,
  Sun,
  CloudRain,
  CloudSnow,
  CloudLightning,
  Loader2,
  RefreshCw,
  MapPin,
  Crosshair,
  Upload
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API } from '../config/api';
import BulkTimerImport from './BulkTimerImport';
import { getCategoryColor } from '../utils/categoryColors';

const SESSION_TYPES = [
  'Entrenamiento 1',
  'Entrenamiento 2',
  'Clasificación 1',
  'Clasificación 2',
  'Serie 1',
  'Serie 2',
  'Serie 3',
  'Final'
];

const CLASSES = ['C1', 'C2', 'C3'];

const AUTO_DELETE_MS = 5 * 60 * 1000; // 5 minutes after countdown hits zero

// Single TimerComponent — driven by `nowMs` (a number) coming from the parent's
// global ticker. NO setInterval inside. All derived values (timeLeft, progress,
// completedAt, auto-delete trigger) are pure functions of the props + a tiny
// piece of "first time we saw diff<=0" memory kept in a ref. This is what
// makes it bullet-proof when there are 10+ instances on screen.
const TimerComponent = React.memo(function TimerComponent({
  timer, nowMs, onDelete, onEdit, onCompleted,
  allTimers, isFullscreen, trackContext,
}) {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  // We persist "the wall-clock instant at which THIS timer first reached
  // zero" on the parent (via onCompleted callback) so re-mounts and re-runs
  // don't reset the auto-delete countdown. timer.completed_at_ms is set by
  // the parent.
  const targetMs = useMemo(() => new Date(timer.target_time).getTime(), [timer.target_time]);
  const createdMs = useMemo(() => new Date(timer.created_at).getTime(), [timer.created_at]);

  const diff = targetMs - nowMs;
  const completed = diff <= 0;
  const timeLeft = Math.max(0, diff);
  const totalTime = Math.max(1, targetMs - createdMs);
  const elapsed = Math.min(totalTime, totalTime - diff);
  const progress = Math.max(0, Math.min(100, (elapsed / totalTime) * 100));

  // First-time-completed side effect: play alarm + notify parent so it can
  // record completed_at_ms (used for auto-delete + display). Guarded by a
  // ref to fire exactly once per mount.
  const alarmFiredRef = useRef(false);
  useEffect(() => {
    if (!completed) return;
    if (alarmFiredRef.current) return;
    alarmFiredRef.current = true;
    if (audioRef.current) {
      audioRef.current.play().catch(() => { /* ignore autoplay blocking */ });
    }
    toast.success(`🏁 ${t('timer.toast_track', { pilot: timer.pilot_name })}`, { duration: 10000 });
    if (typeof onCompleted === 'function' && !timer.completed_at_ms) {
      onCompleted(timer.id, nowMs);
    }
  }, [completed, timer.id, timer.pilot_name, timer.completed_at_ms, nowMs, onCompleted]);

  // Auto-delete after AUTO_DELETE_MS once completed. Runs as a side effect
  // each render — cheap, idempotent. Guarded by `deletedRef` so the DELETE
  // request fires exactly once even though the parent ticker re-renders us
  // every 250ms (otherwise we'd send a flurry of DELETEs in the gap between
  // the call and the state update landing).
  const deletedRef = useRef(false);
  useEffect(() => {
    if (!completed || !timer.completed_at_ms) return;
    if (deletedRef.current) return;
    if (nowMs - timer.completed_at_ms >= AUTO_DELETE_MS) {
      deletedRef.current = true;
      onDelete(timer.id);
    }
  }, [completed, timer.completed_at_ms, timer.id, nowMs, onDelete]);

  const formatTime = (ms) => {
    if (ms <= 0) return `¡${t('timer.to_track')}`;
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getAutoDeleteCountdown = () => {
    if (!timer.completed_at_ms) return null;
    const remaining = Math.max(0, AUTO_DELETE_MS - (nowMs - timer.completed_at_ms));
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const timeBetween = useMemo(() => {
    const pilotTimers = (allTimers || [])
      .filter(t => t.pilot_name === timer.pilot_name && t.id !== timer.id)
      .map(t => ({ ...t, targetMs: new Date(t.target_time).getTime() }))
      .sort((a, b) => a.targetMs - b.targetMs);
    const nextTimer = pilotTimers.find(t => t.targetMs > targetMs);
    if (!nextTimer) return null;
    const d = nextTimer.targetMs - targetMs;
    const h = Math.floor(d / 3600000);
    const m = Math.floor((d % 3600000) / 60000);
    return `${h}h ${m}m`;
  }, [allTimers, timer.id, timer.pilot_name, targetMs]);

  const targetTime = new Date(timer.target_time);

  // Historical C1/C2/C3 keep their colors; any other clase (TC4000, FIAT600,
  // JUNIOR MAX, etc.) gets a stable auto-assigned color from the palette.
  const catColor = getCategoryColor(timer.clase || timer.category);

  const fontSize = isFullscreen ? 'text-2xl' : 'text-sm';
  const timerFontSize = isFullscreen ? 'text-4xl' : 'text-xl';
  const barHeight = isFullscreen ? 'h-16' : 'h-10';
  const padding = isFullscreen ? 'p-4' : 'p-3';

  return (
    <div
      data-testid={`timer-row-${timer.id}`}
      className={`border-l-4 ${catColor.borderL} rounded-lg ${padding} mb-2 ${completed ? 'opacity-60' : ''}`}
      style={{
        backgroundColor: completed
          ? 'color-mix(in srgb, var(--dp-success) 15%, var(--dp-surface-0))'
          : 'var(--dp-surface-0)',
      }}
    >
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleAUSaqTfzahlIxBGntmnaSwZRoKx1KpJFhhLhbm7lUEeCVqJqaSDMxhAfJqfoG0vIEN0lJGPYy8pQHGSkYlkMis9bpKRh2czKj1ukpGHZzMqPW6SkYdnMyo9bpKRh2czKj1ukpGHZzMqPW6SkYdnMyo9bpKRh2czKj1ukpGHZzMqPW6SkYdnMyo9bpKRh2czKj1ukpGHZzMqPW6SkYdn" type="audio/wav" />
      </audio>

      <div className="flex items-center gap-3">
        <div className={`min-w-[160px] ${isFullscreen ? 'min-w-[220px]' : ''}`}>
          <p
            className={`font-semibold ${fontSize} truncate`}
            style={{ color: 'var(--dp-text-primary)' }}
          >
            {timer.pilot_name}
          </p>
          <p
            className={`${isFullscreen ? 'text-base' : 'text-xs'}`}
            style={{ color: 'var(--dp-text-muted)' }}
          >
            {timer.category} | {timer.clase}
            {timer.cajon && <span className="text-yellow-500 ml-2">{t('timer.box_number')}{timer.cajon}</span>}
          </p>
        </div>

        <div
          className={`flex-1 relative ${barHeight} rounded-lg overflow-hidden`}
          style={{ backgroundColor: 'var(--dp-surface-1)' }}
        >
          <div 
            className={`absolute h-full transition-[width,background-color] duration-1000 ${completed ? 'bg-green-600' : 'bg-green-500/70'}`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
          <div
            className={`absolute inset-0 flex items-center justify-around ${isFullscreen ? 'text-lg' : 'text-xs'} px-2 mix-blend-difference text-white`}
          >
            <span className="font-semibold">{timer.session_type}</span>
            <span>{targetTime.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span>
            {timeBetween && <span className="text-yellow-300">{t('timer.next_stint')} {timeBetween}</span>}
            <TimerForecast
              targetTime={timer.target_time}
              trackContext={trackContext}
              isFullscreen={isFullscreen}
            />
          </div>
        </div>

        <div
          className={`min-w-[120px] ${isFullscreen ? 'min-w-[180px]' : ''} text-right tabular-metric ${timerFontSize} font-bold`}
          style={{ color: completed ? 'var(--dp-success)' : 'var(--dp-text-primary)' }}
        >
          {formatTime(timeLeft)}
          {completed && timer.completed_at_ms && (
            <p className="text-xs font-normal" style={{ color: 'var(--dp-text-muted)' }}>
              {t('timer.auto_delete_label')} {getAutoDeleteCountdown()}
            </p>
          )}
        </div>

        {!isFullscreen && (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEdit(timer)}
              data-testid={`timer-edit-${timer.id}`}
              className="h-11 w-11 min-h-[44px] min-w-[44px] p-0 text-blue-400 hover:text-blue-300 active:scale-95"
              aria-label={t('common.edit')}
            >
              <Settings className="w-5 h-5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(timer.id)}
              data-testid={`timer-delete-${timer.id}`}
              className="h-11 w-11 min-h-[44px] min-w-[44px] p-0 text-red-400 hover:text-red-300 active:scale-95"
              aria-label={t('common.delete')}
            >
              <Trash2 className="w-5 h-5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});

const Temporizador = ({ userId }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const logoSrc = theme === 'dark' ? '/datapro-logo-white.png' : '/datapro-logo.png';
  const [timers, setTimers] = useState([]);
  const [allPilots, setAllPilots] = useState({ C1: [], C2: [], C3: [] });
  const [filteredPilots, setFilteredPilots] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);
  
  const [selectedPilot, setSelectedPilot] = useState('');
  const [customPilot, setCustomPilot] = useState('');
  const [category, setCategory] = useState('Turismo Pista');
  const [clase, setClase] = useState('C1');
  const [sessionType, setSessionType] = useState('Entrenamiento 1');
  const [targetDateTime, setTargetDateTime] = useState('');
  const [cajon, setCajon] = useState('');
  // Circuit context from the latest imported race (Turismo Pista / CarShow)
  const [trackContext, setTrackContext] = useState(null);
  // Device GPS (preferred for forecast). null = unknown/not yet resolved.
  const [deviceCoords, setDeviceCoords] = useState(null);
  const [geoStatus, setGeoStatus] = useState('idle'); // idle | prompting | granted | denied | unsupported
  // Bulk-import modal (Cronograma PDF + GRUPOS XLSX → auto-generate all timers)
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    loadTimersFromDB();
    loadPilotsByClass();
    loadTrackContext();
    requestDeviceLocation();
    
    // Single global ticker — 250ms gives a smooth visual update (no half-second
    // stutters) without burning CPU. Every TimerComponent listens via the
    // `nowMs` prop, so 1 timer or 50 timers cost the same.
    const interval = setInterval(() => setCurrentTime(new Date()), 250);
    const now = new Date();
    setTargetDateTime(now.toISOString().slice(0, 16));
    
    const handleFullscreenChange = () =>
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [userId]);

  // Memoized numeric "now" — passed to every TimerComponent. Memoizing on the
  // currentTime Date ensures the children only re-render when the ms changes
  // (cheap reference equality), not because of unrelated parent re-renders.
  const nowMs = useMemo(() => currentTime.getTime(), [currentTime]);

  const requestDeviceLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoStatus('unsupported');
      return;
    }
    setGeoStatus('prompting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDeviceCoords({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          label: 'Mi ubicación actual',
        });
        setGeoStatus('granted');
      },
      (err) => {
        console.warn('Geolocation denied/error:', err?.message || err);
        setGeoStatus('denied');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };

  // Load the most recent geolocated race so the inline per-timer forecast
  // knows which circuit to use (used as a fallback when device GPS is denied).
  const loadTrackContext = async () => {
    try {
      const r = await axios.get(`${API}/races/latest-geolocated`);
      setTrackContext(r.data);
    } catch {
      setTrackContext(null);
    }
  };

  // Prefer device GPS (as requested by user) with automatic fallback to the
  // last imported circuit so the forecast still works when permission is
  // denied or the device has no GPS (desktop).
  const forecastContext = deviceCoords
    ? {
        latitude: deviceCoords.latitude,
        longitude: deviceCoords.longitude,
        circuit: deviceCoords.label,
        location: deviceCoords.label,
        source: 'device',
      }
    : trackContext
    ? { ...trackContext, source: 'circuit' }
    : null;

  useEffect(() => {
    const pilots = allPilots[clase] || [];
    setFilteredPilots(pilots);
    if (pilots.length > 0) {
      setSelectedPilot(pilots[0]);
    } else {
      setSelectedPilot('otro');
    }
  }, [clase, allPilots]);

  const loadTimersFromDB = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/timers?user_id=${userId}`);
      setTimers(response.data);
    } catch (error) {
      console.error('Error loading timers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPilotsByClass = async () => {
    try {
      const { data } = await axios.get(`${API}/races/pilots-by-class`);
      setAllPilots({ C1: data.C1 || [], C2: data.C2 || [], C3: data.C3 || [] });
    } catch (error) {
      setAllPilots({ C1: [], C2: [], C3: [] });
    }
  };

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    const inFS = !!(document.fullscreenElement || document.webkitFullscreenElement) || isFullscreen;
    try {
      if (!inFS) {
        // Try native fullscreen APIs (desktop + Android Chrome)
        if (el?.requestFullscreen) {
          await el.requestFullscreen();
          return;
        }
        if (el?.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
          return;
        }
        // iPad / iPhone Safari: no fullscreen API for divs → use CSS fallback
        setIsFullscreen(true);
        // lock scroll so the page behind doesn't bleed through
        document.body.style.overflow = 'hidden';
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }
        // Always reset CSS fallback state in case it was used
        setIsFullscreen(false);
        document.body.style.overflow = '';
      }
    } catch (e) {
      // If the native API refuses (common on iPadOS Safari) fall back to CSS
      console.warn('Fullscreen API rejected, using CSS fallback:', e);
      setIsFullscreen(prev => !prev);
      document.body.style.overflow = isFullscreen ? '' : 'hidden';
    }
  };

  const addTimer = async () => {
    if (!userId) {
      toast.error('Inicia sesión para guardar timers');
      return;
    }

    let pilotName = selectedPilot;
    if (selectedPilot === 'otro' && customPilot.trim()) {
      pilotName = customPilot.trim();
    }

    if (!pilotName || pilotName === 'otro') {
      toast.error('Selecciona o ingresa un piloto');
      return;
    }

    if (!targetDateTime) {
      toast.error('Selecciona fecha y hora');
      return;
    }

    const targetTime = new Date(targetDateTime);
    if (targetTime <= new Date()) {
      toast.error('La hora debe ser en el futuro');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/timers?user_id=${userId}`, {
        pilot_name: pilotName,
        category,
        clase,
        session_type: sessionType,
        target_time: targetTime.toISOString(),
        cajon: cajon || ''
      });

      setTimers(prev => [...prev, response.data].sort((a, b) => 
        new Date(a.target_time) - new Date(b.target_time)
      ));
      toast.success(`✓ Timer guardado para ${pilotName}`);
      setCajon('');
    } catch (error) {
      toast.error('Error al guardar timer');
    } finally {
      setSaving(false);
    }
  };

  // Memoize handlers so TimerComponent's React.memo equality holds across
  // unrelated parent renders. Without these the children would re-mount on
  // every tick and lose their refs (this is the root cause of the "only 3
  // out of 10 countdowns actually run" bug).
  const deleteTimer = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/timers/${id}?user_id=${userId}`);
      setTimers(prev => prev.filter(t => t.id !== id));
      toast.success('Timer eliminado');
    } catch (error) {
      toast.error('Error al eliminar timer');
    }
  }, [userId]);

  // Called by a TimerComponent the FIRST time its countdown reaches zero.
  // We patch completed_at_ms locally so the 5-min auto-delete clock can run
  // (and survive re-renders). Not persisted server-side — it's transient
  // display state.
  const markCompleted = useCallback((id, ms) => {
    setTimers(prev => prev.map(t => (
      t.id === id && !t.completed_at_ms ? { ...t, completed_at_ms: ms } : t
    )));
  }, []);

  const editTimer = useCallback(async (timer) => {
    const newTimeStr = prompt(
      `Ajustar hora para ${timer.pilot_name}\nFormato: YYYY-MM-DDTHH:mm`,
      new Date(timer.target_time).toISOString().slice(0, 16)
    );

    if (!newTimeStr) return;

    try {
      const newTime = new Date(newTimeStr);
      if (newTime <= new Date()) {
        toast.error('La hora debe ser en el futuro');
        return;
      }

      await axios.put(`${API}/timers/${timer.id}?user_id=${userId}`, {
        pilot_name: timer.pilot_name,
        category: timer.category,
        clase: timer.clase,
        session_type: timer.session_type,
        target_time: newTime.toISOString(),
        cajon: timer.cajon
      });

      setTimers(prev => 
        prev.map(t => t.id === timer.id ? { ...t, target_time: newTime.toISOString(), completed_at_ms: undefined } : t)
          .sort((a, b) => new Date(a.target_time) - new Date(b.target_time))
      );
      toast.success('Timer actualizado');
    } catch (e) {
      toast.error('Error al actualizar');
    }
  }, [userId]);

  const adjustAllTimers = async () => {
    const minutesStr = prompt('¿Cuántos minutos agregar/quitar a TODOS los timers?\n(Usa número negativo para restar)');
    if (!minutesStr) return;
    
    const minutes = parseInt(minutesStr);
    if (isNaN(minutes)) {
      toast.error('Ingresa un número válido');
      return;
    }

    try {
      await axios.post(`${API}/timers/adjust-all?user_id=${userId}&minutes=${minutes}`);
      await loadTimersFromDB();
      toast.success(`Se ajustaron ${minutes} minutos a todos los timers`);
    } catch (error) {
      toast.error('Error al ajustar timers');
    }
  };

  const removeCompleted = async () => {
    try {
      await axios.delete(`${API}/timers/completed/all?user_id=${userId}`);
      await loadTimersFromDB();
      toast.success('Timers cumplidos eliminados');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Fullscreen Layout
  if (isFullscreen) {
    return (
      <div
        ref={containerRef}
        className="fixed inset-0 p-6 overflow-auto"
        style={{ backgroundColor: 'var(--dp-bg)' }}
        data-testid="timer-fullscreen"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <img
              src={logoSrc}
              alt="DATAPRO Racing"
              data-testid="timer-fullscreen-logo"
              className="h-14 md:h-16 w-auto object-contain"
            />
            <div className="text-xl tracking-wider" style={{ color: 'var(--dp-text-muted)' }}>{t('timer.fullscreen_subtitle')}</div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-lg" style={{ color: 'var(--dp-text-secondary)' }}>{t('timer.current_time')}</p>
              <p className="text-6xl font-bold tabular-metric text-yellow-400">
                {currentTime.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            </div>
            <Button
              onClick={toggleFullscreen}
              data-testid="timer-exit-fullscreen-btn"
              className="h-12 min-w-[48px] active:scale-95"
              style={{ backgroundColor: 'var(--dp-surface-1)', color: 'var(--dp-text-primary)' }}
              aria-label={t('timer.fullscreen')}
            >
              <Minimize className="w-6 h-6" />
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {timers.length === 0 ? (
            <div className="text-center py-24" style={{ color: 'var(--dp-text-muted)' }}>
              <Car className="w-24 h-24 mx-auto mb-6 opacity-30" />
              <p className="text-2xl">{t('timer.empty_title')}</p>
            </div>
          ) : (
            timers.map(timer => (
              <TimerComponent
                key={timer.id}
                timer={timer}
                nowMs={nowMs}
                onDelete={deleteTimer}
                onEdit={editTimer}
                onCompleted={markCompleted}
                allTimers={timers}
                isFullscreen={true}
                trackContext={forecastContext}
              />
            ))
          )}
        </div>

        <div className="fixed bottom-4 left-6 flex gap-6 text-sm" style={{ color: 'var(--dp-text-muted)' }}>
          <div className="flex items-center gap-2"><div className="w-4 h-4 bg-yellow-500 rounded" /><span>{t('timer.legend_c1')}</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-500 rounded" /><span>{t('timer.legend_c2')}</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 bg-red-500 rounded" /><span>{t('timer.legend_c3')}</span></div>
        </div>
      </div>
    );
  }

  // Normal Layout
  return (
    <div ref={containerRef} className="space-y-4">
      <Card
        className="border-red-600"
        style={{
          background: 'linear-gradient(to right, color-mix(in srgb, var(--dp-primary) 20%, var(--dp-surface-0)), var(--dp-surface-0))',
        }}
        data-testid="timer-header-card"
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2" style={{ color: 'var(--dp-text-primary)' }}>
                <Timer className="w-7 h-7" style={{ color: 'var(--dp-primary)' }} />
                {t('timer.title')}
                <Badge className="bg-green-600 text-xs ml-2">☁️ {t('timer.cloud_saved')}</Badge>
              </CardTitle>
              <CardDescription style={{ color: 'var(--dp-text-secondary)' }}>
                {t('timer.subtitle')}
              </CardDescription>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm" style={{ color: 'var(--dp-text-secondary)' }}>{t('timer.current_time')}</p>
                <p className="text-4xl font-bold tabular-metric text-yellow-400">
                  {currentTime.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </p>
              </div>
              <Button
                onClick={toggleFullscreen}
                variant="outline"
                data-testid="timer-fullscreen-btn"
                className="h-12 min-w-[48px] active:scale-95"
                style={{ borderColor: 'var(--dp-border)', color: 'var(--dp-text-secondary)' }}
              >
                <Maximize className="w-5 h-5 mr-2" />
                {t('timer.fullscreen')}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card
        className="border-red-900/30"
        style={{ backgroundColor: 'var(--dp-surface-0)' }}
        data-testid="timer-form-card"
      >
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="min-w-[80px]">
              <label className="text-xs mb-1 block" style={{ color: 'var(--dp-text-secondary)' }}>{t('timer.class')}</label>
              <Select value={clase} onValueChange={setClase}>
                <SelectTrigger
                  className="h-12 min-h-[48px]"
                  style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
                  data-testid="timer-class-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)' }}>
                  {CLASSES.map(c => (
                    <SelectItem key={c} value={c} style={{ color: 'var(--dp-text-primary)' }}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[180px]">
              <label className="text-xs mb-1 block" style={{ color: 'var(--dp-text-secondary)' }}>
                {t('timer.pilot')} ({t('timer.pilot_count', { count: filteredPilots.length, class: clase })})
              </label>
              <Select value={selectedPilot} onValueChange={setSelectedPilot}>
                <SelectTrigger
                  className="h-12 min-h-[48px]"
                  style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
                  data-testid="timer-pilot-select"
                >
                  <SelectValue placeholder={t('timer.pilot_select_placeholder')} />
                </SelectTrigger>
                <SelectContent className="max-h-60" style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)' }}>
                  {filteredPilots.map(pilot => (
                    <SelectItem key={pilot} value={pilot} style={{ color: 'var(--dp-text-primary)' }}>{pilot}</SelectItem>
                  ))}
                  <SelectItem value="otro" className="text-yellow-500">{t('timer.pilot_other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedPilot === 'otro' && (
              <div className="min-w-[150px]">
                <label className="text-xs mb-1 block" style={{ color: 'var(--dp-text-secondary)' }}>{t('timer.custom_name')}</label>
                <Input
                  value={customPilot}
                  onChange={(e) => setCustomPilot(e.target.value)}
                  placeholder={t('timer.custom_name')}
                  data-testid="timer-custom-name-input"
                  className="h-12 min-h-[48px]"
                  style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
                />
              </div>
            )}

            <div className="min-w-[140px]">
              <label className="text-xs mb-1 block" style={{ color: 'var(--dp-text-secondary)' }}>{t('timer.category')}</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  className="h-12 min-h-[48px]"
                  style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
                  data-testid="timer-category-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)' }}>
                  <SelectItem value="Turismo Pista" style={{ color: 'var(--dp-text-primary)' }}>Turismo Pista</SelectItem>
                  <SelectItem value="CarShow Santafesino" style={{ color: 'var(--dp-text-primary)' }}>CarShow Santafesino</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[140px]">
              <label className="text-xs mb-1 block" style={{ color: 'var(--dp-text-secondary)' }}>{t('timer.session')}</label>
              <Select value={sessionType} onValueChange={setSessionType}>
                <SelectTrigger
                  className="h-12 min-h-[48px]"
                  style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
                  data-testid="timer-session-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)' }}>
                  {SESSION_TYPES.map(s => (<SelectItem key={s} value={s} style={{ color: 'var(--dp-text-primary)' }}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-[80px]">
              <label className="text-xs mb-1 block" style={{ color: 'var(--dp-text-secondary)' }}>{t('timer.box')}</label>
              <Input
                type="number"
                min="1"
                max="50"
                value={cajon}
                onChange={(e) => setCajon(e.target.value)}
                placeholder="#"
                data-testid="timer-slot-input"
                className="h-12 min-h-[48px]"
                style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
              />
            </div>

            <div className="min-w-[180px]">
              <label className="text-xs mb-1 block" style={{ color: 'var(--dp-text-secondary)' }}>{t('timer.datetime')}</label>
              <Input
                type="datetime-local"
                value={targetDateTime}
                onChange={(e) => setTargetDateTime(e.target.value)}
                data-testid="timer-datetime-input"
                className="h-12 min-h-[48px]"
                style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
              />
            </div>

            <Button
              onClick={addTimer}
              disabled={saving}
              data-testid="timer-add-btn"
              className="h-12 min-h-[48px] min-w-[48px] bg-red-600 hover:bg-red-700 text-white active:scale-95 shadow-md"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              {t('timer.add')}
            </Button>

            <Button
              onClick={removeCompleted}
              variant="outline"
              data-testid="timer-clear-completed-btn"
              className="h-12 min-h-[48px] min-w-[48px] active:scale-95"
              style={{ borderColor: 'var(--dp-border)', color: 'var(--dp-text-secondary)' }}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {t('timer.clear_completed')}
            </Button>

            <Button
              onClick={adjustAllTimers}
              data-testid="timer-adjust-btn"
              className="h-12 min-h-[48px] min-w-[48px] bg-green-600 hover:bg-green-700 text-white active:scale-95 shadow-md"
            >
              <Settings className="w-4 h-4 mr-1" />
              {t('timer.adjust')}
            </Button>

            <Button
              onClick={() => setBulkOpen(true)}
              variant="outline"
              className="h-12 min-h-[48px] min-w-[48px] border-red-800 text-red-300 hover:bg-red-950/40 relative active:scale-95"
              data-testid="bulk-timer-open-btn"
            >
              <Upload className="w-4 h-4 mr-1" />
              {t('timer.load_schedule')}
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md leading-none shadow-md">
                {t('timer.new_badge')}
              </span>
            </Button>

            <Button
              onClick={loadTimersFromDB}
              variant="ghost"
              data-testid="timer-refresh-btn"
              className="h-12 min-h-[48px] min-w-[48px] active:scale-95"
              style={{ color: 'var(--dp-text-secondary)' }}
              aria-label={t('common.loading')}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <BulkTimerImport
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onCreated={() => loadTimersFromDB()}
      />

      <Card
        className="border-red-900/30"
        style={{ backgroundColor: 'var(--dp-surface-0)' }}
        data-testid="timer-list-card"
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: 'var(--dp-text-primary)' }}>
              <Flag className="w-5 h-5" style={{ color: 'var(--dp-primary)' }} />
              {t('timer.next_starts')}
              {timers.length > 0 && <Badge className="bg-red-600 text-white ml-2 tabular-metric">{timers.length}</Badge>}
            </CardTitle>
            <ForecastSourceBadge
              geoStatus={geoStatus}
              forecastContext={forecastContext}
              onRetryGPS={requestDeviceLocation}
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: 'var(--dp-primary)' }} />
              <p className="mt-2" style={{ color: 'var(--dp-text-secondary)' }}>{t('timer.loading')}</p>
            </div>
          ) : timers.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'var(--dp-text-muted)' }}>
              <Car className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>{t('timer.empty_title')}</p>
              <p className="text-sm mt-1">{t('timer.empty_hint')}</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="timer-list">
              {timers.map(timer => (
                <TimerComponent key={timer.id} timer={timer} nowMs={nowMs} onDelete={deleteTimer} onEdit={editTimer} onCompleted={markCompleted} allTimers={timers} isFullscreen={false} trackContext={forecastContext} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-4 text-xs justify-center" style={{ color: 'var(--dp-text-muted)' }}>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-yellow-500 rounded" /><span>{t('timer.legend_c1')}</span></div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-500 rounded" /><span>{t('timer.legend_c2')}</span></div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded" /><span>{t('timer.legend_c3')}</span></div>
      </div>
    </div>
  );
};

export default Temporizador;

// ---------------------------------------------------------------------------
// TimerForecast: pronóstico estimado (Open-Meteo) para el instante exacto en
// que el timer dice "A PISTA". Se muestra inline dentro de cada "Próxima
// Salida" para evitar mezclar el clima actual con el clima previsto.
// ---------------------------------------------------------------------------

const forecastIcon = (condition, size = 'w-4 h-4') => {
  const c = (condition || '').toLowerCase();
  if (c.includes('tormenta')) return <CloudLightning className={`${size} text-yellow-300`} />;
  if (c.includes('lluvia') || c.includes('chaparr') || c.includes('llovizna')) return <CloudRain className={`${size} text-blue-400`} />;
  if (c.includes('nevada')) return <CloudSnow className={`${size} text-white`} />;
  if (c.includes('nub')) return <Cloud className={`${size} text-gray-400`} />;
  return <Sun className={`${size} text-yellow-400`} />;
};

// Module-level forecast cache shared by all TimerForecast instances on screen.
// Open-Meteo returns hourly data, so two timers at the same circuit within
// the same calendar HOUR get the same forecast — we de-dupe both the cache
// entry and any in-flight request. Cache evicts after 10 min so we still
// pick up newer forecasts as the day evolves.
const FORECAST_CACHE = new Map();   // key -> { data, t }
const FORECAST_INFLIGHT = new Map(); // key -> Promise<data>
const FORECAST_TTL_MS = 10 * 60 * 1000;
const FORECAST_CACHE_MAX = 500;      // LRU cap: never grow past this many keys.

function _evictExpiredAndTrim() {
  const now = Date.now();
  // Drop expired entries first.
  for (const [k, v] of FORECAST_CACHE) {
    if (now - v.t >= FORECAST_TTL_MS) FORECAST_CACHE.delete(k);
  }
  // If still over the cap, evict the oldest — Map preserves insertion order,
  // so the first key is the least-recently-inserted.
  while (FORECAST_CACHE.size > FORECAST_CACHE_MAX) {
    const oldest = FORECAST_CACHE.keys().next().value;
    if (oldest === undefined) break;
    FORECAST_CACHE.delete(oldest);
  }
}

function forecastCacheKey(lat, lon, isoAt) {
  // Bucket by hour and by 0.01° grid (≈1 km) so nearby timers share results.
  const hour = isoAt.slice(0, 13);
  const la = Math.round(lat * 100) / 100;
  const lo = Math.round(lon * 100) / 100;
  return `${la},${lo}@${hour}`;
}

async function fetchForecastCached(lat, lon, isoAt) {
  const key = forecastCacheKey(lat, lon, isoAt);
  const hit = FORECAST_CACHE.get(key);
  if (hit && (Date.now() - hit.t) < FORECAST_TTL_MS) {
    // Refresh LRU order: re-inserting moves the key to the end.
    FORECAST_CACHE.delete(key);
    FORECAST_CACHE.set(key, hit);
    return hit.data;
  }
  if (FORECAST_INFLIGHT.has(key)) return FORECAST_INFLIGHT.get(key);
  const p = axios.get(`${API}/forecast`, { params: { lat, lon, at: isoAt } })
    .then(r => {
      FORECAST_CACHE.set(key, { data: r.data, t: Date.now() });
      FORECAST_INFLIGHT.delete(key);
      _evictExpiredAndTrim();
      return r.data;
    })
    .catch(err => {
      FORECAST_INFLIGHT.delete(key);
      throw err;
    });
  FORECAST_INFLIGHT.set(key, p);
  return p;
}

const TimerForecast = ({ targetTime, trackContext, isFullscreen }) => {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [outOfRange, setOutOfRange] = useState(false);

  useEffect(() => {
    if (!trackContext || !targetTime) {
      setForecast(null);
      setOutOfRange(false);
      return;
    }
    const target = new Date(targetTime);
    if (isNaN(target.getTime())) return;
    const daysAhead = (target - new Date()) / 86400000;
    if (daysAhead > 15 || daysAhead < -1) {
      setForecast(null);
      setOutOfRange(true);
      return;
    }
    setOutOfRange(false);
    let cancelled = false;
    setLoading(true);
    fetchForecastCached(trackContext.latitude, trackContext.longitude, target.toISOString())
      .then(data => { if (!cancelled) setForecast(data); })
      .catch(() => { if (!cancelled) setForecast(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [targetTime, trackContext?.latitude, trackContext?.longitude]);

  if (!trackContext) return null;

  const iconSize = isFullscreen ? 'w-5 h-5' : 'w-4 h-4';
  const textSize = isFullscreen ? 'text-base' : 'text-xs';

  if (loading) {
    return (
      <span
        data-testid="timer-forecast-loading"
        className={`flex items-center gap-1 ${textSize} text-gray-400`}
      >
        <Loader2 className={`${iconSize} animate-spin`} />
        <span>clima…</span>
      </span>
    );
  }
  if (outOfRange) {
    return (
      <span data-testid="timer-forecast-oor" className={`${textSize} text-gray-500 italic`}>
        clima: fuera de rango
      </span>
    );
  }
  if (!forecast) return null;

  return (
    <span
      data-testid="timer-forecast"
      className={`flex items-center gap-1.5 ${textSize} text-cyan-300`}
      title={`${trackContext.circuit || trackContext.location || 'Circuito'} · ${forecast.weather_condition || ''} @ ${new Date(targetTime).toLocaleString('es-AR')}`}
    >
      {forecastIcon(forecast.weather_condition, iconSize)}
      <span className="font-bold text-white tabular-nums">
        {forecast.temperature_c != null ? `${forecast.temperature_c.toFixed(0)}°` : '—'}
      </span>
      <Wind className={`${iconSize} text-gray-400`} />
      <span className="tabular-nums">
        {forecast.wind_speed_kmh != null ? `${forecast.wind_speed_kmh.toFixed(0)}km/h` : '—'}
      </span>
      {forecast.wind_direction_cardinal && (
        <span className="text-gray-500">{forecast.wind_direction_cardinal}</span>
      )}
    </span>
  );
};


// Badge that tells the user where the inline timer forecast is being sampled
// from (device GPS vs. imported circuit) and lets them retry GPS.
const ForecastSourceBadge = ({ geoStatus, forecastContext, onRetryGPS }) => {
  if (geoStatus === 'prompting') {
    return (
      <span className="text-xs text-gray-400 flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" />
        Obteniendo ubicación del dispositivo…
      </span>
    );
  }

  if (forecastContext?.source === 'device') {
    return (
      <span
        data-testid="forecast-source-device"
        className="text-xs text-emerald-400 flex items-center gap-1"
        title="Pronóstico usando el GPS del dispositivo"
      >
        <Crosshair className="w-3 h-3" />
        Clima según tu ubicación
      </span>
    );
  }

  if (forecastContext?.source === 'circuit') {
    return (
      <span
        data-testid="forecast-source-circuit"
        className="text-xs text-gray-400 flex items-center gap-1"
        title={`GPS no disponible · usando ${forecastContext.circuit || forecastContext.location || 'último circuito'}`}
      >
        <MapPin className="w-3 h-3" />
        Clima según circuito
        <button
          onClick={onRetryGPS}
          data-testid="forecast-retry-gps"
          className="ml-1 underline text-emerald-400 hover:text-emerald-300"
        >
          Usar mi ubicación
        </button>
      </span>
    );
  }

  if (geoStatus === 'denied' || geoStatus === 'unsupported') {
    return (
      <span className="text-xs text-gray-500 flex items-center gap-1">
        <MapPin className="w-3 h-3" />
        {geoStatus === 'unsupported'
          ? 'Este navegador no soporta geolocalización'
          : 'Permiso de ubicación denegado'}
        <button
          onClick={onRetryGPS}
          className="ml-1 underline text-emerald-400 hover:text-emerald-300"
        >
          Reintentar
        </button>
      </span>
    );
  }

  return null;
};
